import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { HeaderLoadingService } from '../services/header-loading.service';
import { HttpCancelService } from '../services/http-cancel.service';
import { OfferFileTranslationComponent } from '@shared/modals/offer-file-translation/offer-file-translation.component';
import { BYPASS_CANCELLATION } from '../tokens/bypass-cancellation.token';
import { OFFER_TRANSLATE } from '@shared/tokens/offer-translate.token';
import { SKIP_ERROR_NOTIFICATION } from '@shared/tokens/http-context.tokens';
import { catchError, finalize, takeUntil, throwError, defer, switchMap, EMPTY } from 'rxjs';

export const mainHttpInterceptor: HttpInterceptorFn = (req, next) => {
  const httpCancelService = inject(HttpCancelService);
  const loadingService = inject(HeaderLoadingService);
  const messageService = inject(MessageService);
  const dialog: MatDialog = inject(MatDialog);
  
  const bypass = req.context.get(BYPASS_CANCELLATION);
  const translation = req.context.get(OFFER_TRANSLATE);
  
  // Indicate that a request has started. This will be finalized regardless of modal interaction.
  loadingService.requestStarted();

  // Use defer to create the observable stream conditionally based on 'translation'
  return defer(() => {
    if (translation) {
      // If translation is true, open the dialog and wait for its result
      return dialog.open<OfferFileTranslationComponent, any, string | undefined>(OfferFileTranslationComponent, {
        width: '50rem',
        height: 'auto',
        autoFocus: false,
        enterAnimationDuration: '250ms'
      })
      .afterClosed()
      .pipe(
        switchMap((languageCode: string | boolean | undefined) => {
          if (languageCode) {
            // Clone the request and add the language query parameter
            const separator = req.url.includes('?') ? '&' : '?';
            const modifiedReq = req.clone({
              url: `${req.url}${separator}language=${languageCode}`
            });
            // Proceed with the (potentially modified) request
            return bypass
              ? next(modifiedReq)
              : next(modifiedReq).pipe(takeUntil(httpCancelService.onCancel$));
          } else {
            // No language selected, cancel the request by returning an empty observable.
            return EMPTY;
          }
        })
      );
    } else {
      // If translation is false, proceed with the original request immediately
      return bypass
        ? next(req)
        : next(req).pipe(takeUntil(httpCancelService.onCancel$));
    }
  }).pipe(
    catchError((error: HttpErrorResponse) => {
      const isFetchAbort = error.error instanceof DOMException && error.error.name === 'AbortError';
      const isAngularAbort = error.name === 'HttpErrorResponse' && error.message.includes('AbortError');
      const isXhrAbort = error.status === 0 && navigator.onLine;

      if (!isFetchAbort && !isAngularAbort && !isXhrAbort && !req.context.get(SKIP_ERROR_NOTIFICATION)) {
        messageService.add({
          severity: 'error',
          summary: 'An error occurred:',
          detail: error.message,
        });
      }
      
      return throwError(() => error);
    }),
    finalize(() => {
      loadingService.requestEnded();
    })
  );
};
